import React, { useState, useRef, useEffect } from 'react';
import { useCity } from '../../contexts/cityContexts';
import './CitySelector.css';

interface CitySelectorProps {
	className?: string;
	showLabel?: boolean;
	placeholder?: string;
	onCityChange?: (city: any) => void;
	showOnlyForSuperAdmin?: boolean;
}

const CitySelector: React.FC<CitySelectorProps> = ({
	className = '',
	showLabel = false,
	placeholder = 'Select a city',
	onCityChange,
	showOnlyForSuperAdmin = true,
}) => {
	const {
		cities,
		currentCity,
		setCurrentCity,
		isSuperAdmin,
		canSelectCity,
		userAssignedCity,
		loading,
	} = useCity();

	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// If loading, show loading state
	if (loading) {
		return (
			<div className='city-selector-loading'>
				<span>Loading city...</span>
			</div>
		);
	}

	// If not Super Admin - show read-only city
	if (!isSuperAdmin) {
		const displayCity = currentCity || userAssignedCity;
		return (
			<div className='city-display'>
				<span className='city-label'>City: </span>
				<span className='city-value'>{displayCity?.label || 'Not Assigned'}</span>
				<input type='hidden' name='city' value={displayCity?.value || ''} />
			</div>
		);
	}

	// For Super Admin - show dropdown
	if (cities.length === 0) {
		return null;
	}

	const handleCitySelect = (city: (typeof cities)[0]) => {
		setCurrentCity(city);
		setIsOpen(false);
		if (onCityChange) {
			onCityChange(city);
		}
	};

	return (
		<div className={`city-selector ${className}`} ref={dropdownRef}>
			{showLabel && <label className='city-selector-label'>Select City</label>}
			<div
				className={`city-selector-trigger ${isOpen ? 'open' : ''}`}
				onClick={() => setIsOpen(!isOpen)}>
				<span className='city-selector-value'>{currentCity?.label || placeholder}</span>
				<span className='city-selector-arrow'>▼</span>
			</div>
			{isOpen && (
				<div className='city-selector-dropdown'>
					{cities.map((city) => (
						<div
							key={city._id}
							className={`city-option ${currentCity?._id === city._id ? 'active' : ''}`}
							onClick={() => handleCitySelect(city)}>
							<span>{city.label}</span>
							{currentCity?._id === city._id && <span className='checkmark'>✓</span>}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default CitySelector;
